import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Eye, EyeOff, Mail, Lock, ArrowRight, ShieldCheck, Building2, Sparkles,
  ServerCog, BarChart3, Users, Activity, AlertCircle,
} from "lucide-react";

/**
 * QrewHR Platform Console — dedicated owner login.
 * Strictly for platform_owner / super_platform_admin accounts.
 * Tenant users (HR Admin, Manager, Employee) must use /login.
 */
export default function AdminLogin() {
  const { user, login, logout, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (!u?.is_platform_owner) {
        // Reject — wipe session and warn
        logout();
        toast.error("This portal is restricted to Qrew HR platform owners. Tenant users, please use /login.");
        return;
      }
      toast.success("Welcome to Platform Console");
      nav("/master");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;
  // Already-signed-in platform owner → straight to console
  if (user?.is_platform_owner) return <Navigate to="/master" replace />;

  return (
    <div
      className="min-h-screen relative overflow-hidden flex"
      style={{ background: "linear-gradient(135deg, #0b1023 0%, #1a1147 45%, #2a0d4d 100%)" }}
      data-testid="admin-login-page"
    >
      {/* Glow orbs */}
      <div className="absolute -top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-violet-500/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-fuchsia-500/25 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 left-1/3 w-[20rem] h-[20rem] rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" />

      {/* ============== LEFT — Brand & narrative ============== */}
      <div className="hidden lg:flex relative z-10 flex-1 flex-col justify-between p-10 xl:p-14 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white/10 ring-1 ring-white/15 flex items-center justify-center">
            <img src="/qrewhr-logo.jpg" alt="Qrew HR" className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
          <div>
            <div className="font-bold font-[Outfit] text-xl tracking-tight">Qrew HR</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-violet-200/80 font-bold">Platform Console</div>
          </div>
        </div>

        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 ring-1 ring-white/15 backdrop-blur-sm text-[11px] uppercase tracking-[0.18em] font-bold text-violet-200">
            <ShieldCheck className="w-3.5 h-3.5" /> Owner-only access
          </div>
          <h1 className="mt-4 text-4xl xl:text-5xl font-bold font-[Outfit] tracking-tight leading-[1.05]">
            Run your <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 via-fuchsia-300 to-orange-200">SaaS business</span>,<br/> not just an HR tool.
          </h1>
          <p className="mt-4 text-base text-violet-100/80 leading-relaxed max-w-lg">
            Manage every Qrew HR client from one secure console — tenants, plans, billing, branding, feature controls, and platform health.
          </p>

          {/* Capability chips */}
          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            {[
              { icon: Building2, label: "Tenants & Clients" },
              { icon: BarChart3, label: "Revenue & Usage" },
              { icon: ServerCog, label: "System Health" },
              { icon: Users,     label: "Cross-Tenant Audit" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/5 ring-1 ring-white/10 backdrop-blur-sm" data-testid={`admin-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-400/30 to-fuchsia-400/30 flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-violet-200" />
                </div>
                <span className="text-[12px] font-semibold text-white/90">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] text-violet-200/60 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Encrypted session · SOC 2 Type II · ISO 27001
        </div>
      </div>

      {/* ============== RIGHT — Login card ============== */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-8 text-white">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 ring-1 ring-white/15">
              <img src="/qrewhr-logo.jpg" alt="Qrew HR" className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <div>
              <div className="font-bold font-[Outfit] text-lg">Qrew HR</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-violet-200/80 font-bold">Platform Console</div>
            </div>
          </div>

          <div className="rounded-2xl bg-white shadow-2xl shadow-violet-900/40 ring-1 ring-white/20 p-7 sm:p-8" data-testid="admin-login-card">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] uppercase tracking-[0.15em] font-bold">
              <Sparkles className="w-3 h-3" /> Restricted
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 font-[Outfit]" data-testid="admin-login-title">Platform sign-in</h2>
            <p className="text-sm text-slate-600 mt-1.5">
              Secure access for <span className="font-semibold text-slate-900">Qrew HR platform administrators</span>.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="admin-email" className="text-xs font-semibold text-slate-700">Work Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="admin-email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="owner@qrewhr.com"
                    className="pl-10 h-11"
                    data-testid="admin-login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="admin-password" className="text-xs font-semibold text-slate-700">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="admin-password" type={showPwd ? "text" : "password"} required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 h-11"
                    data-testid="admin-login-password"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label={showPwd ? "Hide password" : "Show password"}>
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit" disabled={submitting}
                className="w-full h-11 text-white font-semibold qh-grad-bg transition-all hover:brightness-110"
                data-testid="admin-login-submit"
              >
                {submitting ? "Signing in…" : <>Enter Platform Console <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>

            {/* Security note */}
            <div className="mt-5 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2.5" data-testid="admin-security-note">
              <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-[11px] text-amber-900 leading-relaxed">
                <span className="font-bold">Restricted area.</span> All actions are audited. Tenant employees must use the
                <Link to="/login" className="ml-1 font-semibold text-violet-700 hover:underline" data-testid="back-to-hrm-login">HRM login</Link>.
              </div>
            </div>

            <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-slate-500">
              <Link to="/login" className="hover:text-violet-700 font-semibold inline-flex items-center gap-1" data-testid="hrm-login-link">
                <Activity className="w-3 h-3" /> Back to HRM login
              </Link>
              <span className="w-px h-3 bg-slate-200" />
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> 2FA · SSO · Audit</span>
            </div>
          </div>

          <div className="mt-6 text-center text-[11px] text-violet-200/70">
            © 2026 Qrew HR Pvt. Ltd. · Platform Console v1.9
          </div>
        </div>
      </div>
    </div>
  );
}
